import React, { useEffect, useId, useRef } from 'react';
import { useGlobalError } from './GlobalErrorProvider';

interface ErrorDisplayProps {
  error: string | null;
  onRetry?: () => void;
  message?: string;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ 
  error, 
  onRetry,
  message = 'Failed to load data' 
}) => {
  const globalError = useGlobalError();
  const noticeId = useId();
  const retryRef = useRef(onRetry);
  retryRef.current = onRetry;

  useEffect(() => {
    if (!globalError) return;
    if (!error) {
      globalError.clearError(noticeId);
      return () => globalError.clearError(noticeId);
    }

    // Suppress publishing error toast while document is hidden (user switched away on mobile)
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      return;
    }

    // Delay publishing error toast by 1500ms to allow foreground refetches to settle silently
    const publishTimer = window.setTimeout(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      globalError.publishError({
        id: noticeId,
        title: message,
        error,
        onRetry: onRetry ? () => retryRef.current?.() : undefined,
      });
    }, 1500);

    return () => {
      window.clearTimeout(publishTimer);
      globalError.clearError(noticeId);
    };
  }, [error, globalError, message, noticeId, onRetry]);

  useEffect(() => {
    // When returning to foreground with an active error, trigger retry automatically
    const handleVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible' && error) {
        retryRef.current?.();
      }
    };
    document.addEventListener('visibilitychange', handleVisible);
    window.addEventListener('focus', handleVisible);
    return () => {
      document.removeEventListener('visibilitychange', handleVisible);
      window.removeEventListener('focus', handleVisible);
    };
  }, [error]);

  if (!error) return null;

  if (globalError) return null;

  return (
    <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 text-foreground px-3 py-2 sm:px-4 sm:py-3">
      <div className="flex items-start gap-3">
        <div className="flex items-center justify-center h-7 w-7 rounded-full bg-rose-500/40 text-xs font-bold text-rose-50">
          !
        </div>
        <div className="flex-1 space-y-1">
          <h3 className="text-xs sm:text-sm font-semibold text-rose-100 uppercase tracking-[0.14em]">
            {message}
          </h3>
          <p className="text-[11px] sm:text-xs text-rose-100/80 break-words">
            {error}
          </p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="mt-2 inline-flex items-center px-3 py-1.5 rounded-lg border border-rose-300/60 bg-rose-500/20 text-[11px] font-semibold uppercase tracking-[0.12em] text-rose-50 hover:bg-rose-500/30 disabled:opacity-50"
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorDisplay;
